import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logos/ICON.png";

const MenuIcon = () => (
  <svg
    width="24"
    height="24"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    width="14"
    height="14"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

/**
 * Navbar Component
 * - Initially transparent, becomes solid background on scroll
 * - Sticky positioning
 * - Logo on left, menu items in center, order button on right
 * - Mobile hamburger menu
 */
function Navbar({
  isScrolled,
  topBarHidden,
  restaurantName,
  phone,
  onHomeClick,
  onMenuClick,
  onAboutClick,
  onCateringClick,
  onContactClick,
  onOrderClick,
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: "HOME", onClick: onHomeClick },
    { label: "ABOUT", onClick: onAboutClick },
    { label: "MENU", onClick: onMenuClick },
    { label: "CATERING", onClick: onCateringClick },
    {
      label: "CONTACT",
      onClick: onContactClick,
      href: "#contact",
    },
  ];

  const handleNavClick = (item) => {
    setMobileMenuOpen(false);
    // If item has onClick handler, use it
    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      // Smooth scroll to section by href with navbar offset
      const element = document.querySelector(item.href);
      if (element) {
        const navbarHeight = 128; // h-32 = 8rem = 128px
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - navbarHeight,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <nav
      className={`w-full transition-all duration-150 ${
        isScrolled ? "bg-slate-900 shadow-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-32">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="transition-colors duration-300"
            >
              <img
                src={logo}
                alt="A+ FLAVOR"
                className="h-24 w-auto"
              />
            </Link>
          </div>
          {/* Desktop Menu */}
          <div className="hidden md:flex flex-1 items-center justify-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item)}
                className={`text-sm font-semibold transition-colors duration-300 ${
                  isScrolled
                    ? "text-white hover:text-lime-400"
                    : "text-white hover:text-amber-200"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Phone - Shows only when TopBar is hidden (after scrolling) */}
          {topBarHidden && (
            <a
              href={`tel:${phone}`}
              className="hidden md:flex items-center gap-2 text-sm font-medium text-white hover:text-gray-100 transition-colors duration-200"
            >
              <PhoneIcon />
              {phone}
            </a>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 transition-colors duration-300 ${
              isScrolled ? "text-white" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className={`md:hidden pb-4 border-t ${
              isScrolled
                ? "border-gray-600 bg-slate-800"
                : "border-white/20 bg-gray-900/95"
            }`}
          >
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item)}
                className={`block w-full text-left px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                  isScrolled
                    ? "text-white hover:text-lime-400 hover:bg-slate-700"
                    : "text-white hover:text-amber-200 hover:bg-gray-800"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
